// eslint-disable-next-line
import React, { useEffect, useState } from 'react'
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting'
import { useSelector } from 'react-redux'
import { AppState } from 'ReducerState'
import { message, Modal, Button, Spin, Empty, Table, Progress, Tooltip } from 'antd'
import { PrinterOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { useMessage } from 'util/messageLocalHook'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPauseCircle, faCheck, faHourglassHalf, faExclamation } from '@fortawesome/free-solid-svg-icons'
import styled from 'styled-components'
import IntlMessages from 'util/IntlMessages'

const TableCustom = styled(Table)`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    font-size: 13px;
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    padding: 10px 8px;
  }

  .ant-table-tbody > tr > td {
    background-color: #ffffff !important;
    border-bottom: 1px solid #f0f0f0;
    border-right: 1px solid #f0f0f0;
    font-size: 13px;
    padding: 10px 8px;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f9f9f9 !important;
  }

  .ant-table-row.row-error > td {
    background-color: #fff1f0 !important;
  }

  .ant-table-row.row-error:hover > td {
    background-color: #ffe8e6 !important;
  }
`

const FontAwesomeIconCustom = styled(FontAwesomeIcon) <{ color?: string }>`
  font-size: 22px;
  color: ${props => props.color || 'inherit'};
`

type ReportStatus = 'pending' | 'running' | 'success' | 'error'

interface ReportItem {
  id: number
  batch_name: string
  batch_type: string
  output_type: string
  output_target: string
  enabled: boolean
  report_count: number
  status?: ReportStatus
  message?: string
  execute_time?: number
}

const CloseShiftModal = ({ isModalVisible, setIsModalVisible }: any) => {

  const { t } = useMessage()

  const { auth } = useSelector((props: AppState) => props)
  const { authUser } = auth
  const current_shift = (authUser as any)?.current_shift

  const [closeShiftReports, setCloseShiftReports] = useState<ReportItem[]>([])
  const [loadingData, setLoadingData] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [overallProgress, setOverallProgress] = useState(0)
  const [elapsedTime, setElapsedTime] = useState(0)
  const timerRef = React.useRef<any>(null)

  // 🖨️ Printer modal state
  const [isPrinterModalOpen, setIsPrinterModalOpen] = useState(false);
  const [printerLoading, setPrinterLoading] = useState(false);
  const [printerError, setPrinterError] = useState<string | null>(null);
  const [defaultPrinterName, setDefaultPrinterName] = useState<string | null>(null);
  const [printers, setPrinters] = useState<Array<any>>([]);
  const [selectedPrinterName, setSelectedPrinterName] = useState<string | null>(
    () => localStorage.getItem('selected_printer_closeshift') || null
  );
  const [currentPrintingBatch, setCurrentPrintingBatch] = useState<ReportItem | null>(null);
  const [waitingForPrinter, setWaitingForPrinter] = useState(false);
  const reportCountToRun = closeShiftReports.reduce((sum, report) => sum + (Number(report.report_count) || 0), 0);
  const hasReportsToRun = closeShiftReports.length > 0 && reportCountToRun > 0;

  // Fetch close shift reports using new dedicated API
  const fetchCloseShiftReports = async () => {
    setLoadingData(true)
    try {
      const token = localStorage.getItem('access_token') || '';
      const headers: HeadersInit = {
        'x-api-key': API_PMS_HEADERS.headers['x-api-key'] ?? '',
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${API_URL_V1}pms/rpc/report/get_batch_reports`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ batch_type: 'closeshift' }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}`);
      }

      const json = await response.json();
      const reports = (json?.data?.result_data || []).map((r: any) => ({
        ...r,
        status: 'pending' as ReportStatus,
        message: '',
        execute_time: 0
      }));
      setCloseShiftReports(reports);
    } catch (err: any) {
      console.error('[CloseShift] Failed to fetch reports:', err);
      message.error(err?.message || 'Failed to load close shift reports');
      setCloseShiftReports([]);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (isModalVisible) {
      fetchCloseShiftReports();
      setOverallProgress(0);
      setElapsedTime(0);
      setIsRunning(false);
    }
  }, [isModalVisible])

  const callCloseShiftAPI = async () => {
    try {
      const prop_id = (authUser as any)?.prop_id;
      const shift = current_shift || 'A';

      if (!prop_id) {
        throw new Error('Property ID not found in user session');
      }

      const token = localStorage.getItem('access_token') || '';
      const headers: HeadersInit = {
        'x-api-key': API_PMS_HEADERS.headers['x-api-key'] ?? '',
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${API_URL_V1}pms/rpc/close/shift`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ prop_id, shift }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}`);
      }

      const json = await response.json();
      const resultMsg = json?.data?.result_msg || json?.message || 'Shift closed successfully';

      message.success(resultMsg);
      return { success: true, message: resultMsg };
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to close shift';
      message.error(errorMsg);
      return { success: false, message: errorMsg };
    }
  };

  const fetchDefaultPrinter = async (): Promise<string | null> => {
    try {
      const res = await fetch('https://localhost:7190/api/Printer/getDefaultPrinter', { method: 'GET' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      return json?.defaultPrinter || null;
    } catch (e: any) {
      throw new Error(e?.message || 'Failed to fetch default printer');
    }
  };

  const fetchPrinterList = async (): Promise<Array<any>> => {
    try {
      const res = await fetch('https://localhost:7190/api/Printer/getPrinterList', { method: 'GET' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      return (Array.isArray(json) ? json : []).map((p: any) => ({
        name: p?.name,
        status: p?.status,
        isDefault: !!p?.isDefault,
        driverName: p?.driverName,
        portName: p?.portName,
        queueCount: p?.queueCount ?? 0,
      }));
    } catch (e: any) {
      throw new Error(e?.message || 'Failed to fetch printer list');
    }
  };

  const updateReportStatus = (id: number, status: ReportStatus, msg?: string, execTime?: number) => {
    setCloseShiftReports(prev => prev.map(r =>
      r.id === id ? { ...r, status, message: msg || r.message, execute_time: execTime ?? r.execute_time } : r
    ));
  };

  const runSingleReport = async (report: ReportItem): Promise<{ success: boolean; message?: string }> => {
    const startTime = Date.now();
    updateReportStatus(report.id, 'running');

    try {
      const token = localStorage.getItem('access_token') || '';

      const res = await fetch(`${API_URL_V1}pms/report/batch/run?active=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ batchID: [report.id], userName: (authUser as any)?.user_name }),
      });

      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const json = await res.json().catch(() => ({}));
      const msg = (json?.message || 'Success').toString();

      updateReportStatus(report.id, 'success', msg, parseFloat(elapsed));
      return { success: true, message: msg };
    } catch (err: any) {
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
      const errorMsg = err?.message || 'Failed';
      updateReportStatus(report.id, 'error', errorMsg, parseFloat(elapsed));
      return { success: false, message: errorMsg };
    }
  };

  const printBatch = async (report: ReportItem, printerName: string): Promise<{ success: boolean; message?: string }> => {
    const startTime = Date.now();
    updateReportStatus(report.id, 'running');

    try {
      const token = localStorage.getItem('access_token') || '';
      const res = await fetch('https://localhost:7190/api/Printer/printBatchDocument', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ batch_id: Number(report.id), export_target: printerName }),
      });

      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);

      if (!res.ok && res.status !== 206) throw new Error('HTTP ' + res.status);

      const json = await res.json().catch(() => null);

      if (res.status === 206 || json?.status === 206) {
        const errorMsg = json?.message || 'Partial failure';
        updateReportStatus(report.id, 'error', errorMsg, parseFloat(elapsed));
        return { success: false, message: errorMsg };
      }

      const msg = json?.message || 'Print Success';
      updateReportStatus(report.id, 'success', msg, parseFloat(elapsed));
      return { success: true, message: msg };
    } catch (err: any) {
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
      const errorMsg = err?.message || 'Print Failed';
      updateReportStatus(report.id, 'error', errorMsg, parseFloat(elapsed));
      return { success: false, message: errorMsg };
    }
  };

  const openPrinterModal = async (report: ReportItem): Promise<{ success: boolean; message?: string }> => {
    return new Promise((resolve) => {
      setCurrentPrintingBatch(report);
      setIsPrinterModalOpen(true);
      setPrinterLoading(true);
      setPrinterError(null);
      setWaitingForPrinter(true);

      const loadPrinters = async () => {
        try {
          const [def, list] = await Promise.all([
            fetchDefaultPrinter(),
            fetchPrinterList(),
          ]);
          setDefaultPrinterName(def);
          setPrinters(list);
          const defName = (localStorage.getItem('selected_printer_closeshift')) || def || list.find(x => x.isDefault)?.name || null;
          setSelectedPrinterName(defName);
        } catch (err: any) {
          setPrinterError(err?.message || 'Failed to get printers');
        } finally {
          setPrinterLoading(false);
        }
      };

      loadPrinters();

      // Store resolve for later use when printer is selected
      (window as any).__printerResolve = resolve;
    });
  };

  const handleChoosePrinter = async (printerName: string) => {
    setSelectedPrinterName(printerName);
    localStorage.setItem('selected_printer_closeshift', printerName);
    message.success(`Selected printer: ${printerName}`);
    setIsPrinterModalOpen(false);
    setWaitingForPrinter(false);

    if (currentPrintingBatch) {
      const result = await printBatch(currentPrintingBatch, printerName);

      // Resolve the promise
      if ((window as any).__printerResolve) {
        (window as any).__printerResolve(result);
        delete (window as any).__printerResolve;
      }
    }
  };

  const showErrorModal = (report: ReportItem, errorMsg: string): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: t("app.global.label.HandleError"),
        zIndex: 10002,
        icon: <FontAwesomeIcon icon={faExclamation} style={{ color: '#ff4d4f' }} />,
        content: (
          <div>
            <p><strong>{t("closeShift.error.report")}:</strong> {report.batch_name}</p>
            <p><strong>{t("closeShift.error.error")}:</strong> {errorMsg}</p>
            <p style={{ marginTop: 16 }}>{t("closeShift.message.continueAnyway")}</p>
          </div>
        ),
        okText: t("app.global.bt.submit"),
        cancelText: t("app.global.bt.cancel"),
        onOk: async () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        }
      });
    });
  };

  const runAllReports = async () => {
    if (!current_shift) {
      Modal.warning({
        title: t("app.global.label.Warning"),
        zIndex: 10002,
        content: (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>{t("closeShift.warning.selectShiftFirst")}</p>
            <p style={{ color: '#8c8c8c', fontSize: 14 }}>{t("closeShift.label.currentShift")}: <strong style={{ color: '#ff4d4f' }}>{t("closeShift.label.notSelected")}</strong></p>
          </div>
        ),
      });
      return;
    }

    setIsRunning(true);
    setOverallProgress(0);
    setElapsedTime(0);

    // Start timer
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    // Reset all reports to pending
    setCloseShiftReports(prev => prev.map(r => ({ ...r, status: 'pending' as ReportStatus, message: '', execute_time: 0 })));

    const total = closeShiftReports.length;
    let completed = 0;
    let hasError = false;
    let shouldCloseShift = false;

    for (const report of closeShiftReports) {
      // Handle printer type
      if (report.output_type.toLowerCase() === 'printer') {
        try {
          const result = await openPrinterModal(report);

          if (!result.success) {
            hasError = true;
            shouldCloseShift = await showErrorModal(report, result.message || 'Print failed');
            if (!shouldCloseShift) break;
          }
        } catch (err: any) {
          updateReportStatus(report.id, 'error', err?.message || 'Printer failed', 0);
          hasError = true;
          shouldCloseShift = await showErrorModal(report, err?.message || 'Printer failed');
          if (!shouldCloseShift) break;
        }
      } else {
        // Handle email/file types
        const result = await runSingleReport(report);
        if (!result.success) {
          hasError = true;
          shouldCloseShift = await showErrorModal(report, result.message || 'Failed');
          if (!shouldCloseShift) break;
        }
      }

      completed++;
      setOverallProgress(Math.round((completed / total) * 100));
    }

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsRunning(false);

    // Handle results
    if (!hasError || shouldCloseShift) {
      // Call close shift API
      const result = await callCloseShiftAPI();
      if (result.success) {
        setTimeout(() => {
          setIsModalVisible(false);
        }, 1500);
      }
    }
  };

  const handleStart = () => {
    if (!current_shift) {
      Modal.warning({
        title: t("app.global.label.Warning"),
        zIndex: 10002,
        content: (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>{t("closeShift.warning.selectShiftFirst")}</p>
            <p style={{ color: '#8c8c8c', fontSize: 14 }}>{t("closeShift.label.currentShift")}: <strong style={{ color: '#ff4d4f' }}>{t("closeShift.label.notSelected")}</strong></p>
          </div>
        ),
      });
      return;
    }

    Modal.confirm({
      title: t("closeShift.modal.startTitle"),
      zIndex: 10002,
      content: (
        <div>
          <p><strong>{t("closeShift.label.currentShift")}:</strong> {current_shift}</p>
          <p><strong>{t("closeShift.label.reportsToRun")}:</strong> {reportCountToRun}</p>
          <p style={{ marginTop: 16 }}>{t("closeShift.message.confirmStart")}</p>
        </div>
      ),
      okText: t("app.global.bt.run"),
      cancelText: t("app.global.bt.cancel"),
      onOk: () => {
        void runAllReports();
      },
    });
  };

  const checkFontStatus = (status?: ReportStatus) => {
    switch (status) {
      case "pending":
        return <FontAwesomeIconCustom icon={faPauseCircle} color="cornflowerblue" />
      case "error":
        return <FontAwesomeIconCustom icon={faExclamation} color="red" />
      case "success":
        return <FontAwesomeIconCustom icon={faCheck} color="cadetblue" />
      case "running":
        return <FontAwesomeIconCustom icon={faHourglassHalf} spin={true} color="cadetblue" />
      default:
        return <FontAwesomeIconCustom icon={faPauseCircle} color="cornflowerblue" />
    }
  };

  const columns: any = [
    {
      title: <IntlMessages id="app.global.label.title" />,
      dataIndex: "batch_name",
      key: "batch_name",
      render: (text: string, record: ReportItem) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            <span style={{ marginRight: 12 }}><IntlMessages id="closeShift.label.output" />: {record.output_type}</span>
            <span><IntlMessages id="closeShift.label.reports" />: {record.report_count}</span>
          </div>
        </div>
      )
    },
    {
      title: <IntlMessages id="app.global.label.message" />,
      dataIndex: "message",
      key: "message",
      render: (text: string) => (
        <Tooltip title={text || '-'}>
          <div style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: text ? '#262626' : '#bfbfbf'
          }}>
            {text || '-'}
          </div>
        </Tooltip>
      )
    },
    {
      title: <IntlMessages id="app.global.label.executeTime" />,
      dataIndex: "execute_time",
      key: "execute_time",
      className: "col-width-120",
      align: 'center',
      render: (time: number) => (
        <div>{time ? `${time} ` : ''}<IntlMessages id="closeShift.unit.seconds" /></div>
      )
    },
    {
      title: <IntlMessages id="app.global.label.status" />,
      dataIndex: "status",
      key: "status",
      className: "col-width-100",
      align: "center",
      render: (_: any, record: ReportItem) => checkFontStatus(record.status)
    }
  ];

  const mapStatusMeta = (status?: string) => {
    const s = String(status || '').toLowerCase();
    if (s === 'ready') return { label: 'Ready', color: '#52c41a' };
    if (s === 'paused' || s === 'offline') return { label: status || 'Paused', color: '#faad14' };
    if (s === 'error') return { label: 'Error', color: '#ff4d4f' };
    return { label: status || 'Unknown', color: '#d9d9d9' };
  };

  const onAfterClose = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setOverallProgress(0);
    setElapsedTime(0);
    setIsRunning(false);
    setCloseShiftReports([]);
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '40px' }}>
            <span style={{ fontSize: 18, fontWeight: 600 }}><IntlMessages id="closeShift.modal.title" /></span>
            <span style={{
              fontSize: 14,
              fontWeight: 600,
              color: current_shift ? '#1890ff' : '#ff4d4f',
              background: current_shift ? '#e6f7ff' : '#fff1f0',
              padding: '4px 12px',
              borderRadius: '4px',
              border: `1px solid ${current_shift ? '#91d5ff' : '#ffa39e'}`
            }}>
              <IntlMessages id="closeShift.label.shift" />: {current_shift || <IntlMessages id="closeShift.label.notSelected" />}
            </span>
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        afterClose={onAfterClose}
        maskClosable={!isRunning}
        width="min(960px, 95vw)"
        zIndex={10000}
        style={{ top: 20 }}
        styles={{ body: { padding: '16px' } }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setIsModalVisible(false)} disabled={isRunning}>
              <IntlMessages id="app.global.bt.close" />
            </Button>
            <Button
              type="primary"
              onClick={handleStart}
              disabled={loadingData || isRunning || !current_shift}
              loading={isRunning}
              style={{ marginLeft: 8 }}
            >
              {isRunning ? <IntlMessages id="app.global.label.Processing" /> : <IntlMessages id="app.global.bt.run" />}
            </Button>
          </div>
        }
      >
        {!current_shift && (
          <div style={{
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <FontAwesomeIcon icon={faExclamation} style={{ color: '#fa8c16', fontSize: 18 }} />
            <span style={{ color: '#d46b08' }}><IntlMessages id="closeShift.warning.selectShiftBeforeStart" /></span>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}><IntlMessages id="closeShift.label.progress" /></span>
            <span style={{ fontSize: 14, color: '#8c8c8c' }}>
              <ClockCircleOutlined style={{ marginRight: 6 }} />
              {elapsedTime} <IntlMessages id="closeShift.unit.seconds" />
            </span>
          </div>
          <Progress
            percent={overallProgress}
            status={isRunning ? 'active' : overallProgress === 100 ? 'success' : undefined}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
          />
        </div>

        {loadingData ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : (!closeShiftReports || !closeShiftReports.length) ? (
          <Empty description={t("closeShift.empty.noReports")} />
        ) : (
          <TableCustom
            dataSource={closeShiftReports}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
            rowClassName={(record: any) => record.status === 'error' ? 'row-error' : ''}
          />
        )}
      </Modal>

      {/* Printer selection modal */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <PrinterOutlined style={{ fontSize: 18, color: '#2f6fe4' }} />
              <span style={{ fontWeight: 700, color: '#2f6fe4' }}><IntlMessages id="closeShift.printer.title" /></span>
            </div>
          </div>
        }
        open={isPrinterModalOpen}
        onCancel={() => {
          setIsPrinterModalOpen(false);
          setWaitingForPrinter(false);
          if (currentPrintingBatch) {
            updateReportStatus(currentPrintingBatch.id, 'error', 'Print cancelled', 0);
            // Reject the promise
            if ((window as any).__printerResolve) {
              (window as any).__printerResolve({ success: false, message: 'Print cancelled' });
              delete (window as any).__printerResolve;
            }
          }
        }}
        zIndex={10001}
        footer={null}
        width="min(680px, 95vw)"
        styles={{ body: { padding: '16px', maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {printerLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
            <Spin />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {printerError && (
              <div style={{
                background: '#fff1f0', border: '1px solid #ffa39e', color: '#a8071a', padding: 10,
                borderRadius: 8
              }}>
                {printerError}
              </div>
            )}
            {(printers || []).map((p: any, idx: number) => {
              const meta = mapStatusMeta(p?.status);
              const isReady = String(p?.status || '').toLowerCase() === 'ready';
              const isDefault = defaultPrinterName ? p?.name === defaultPrinterName : !!p?.isDefault;
              return (
                <div key={idx} style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 16,
                  alignItems: 'center',
                  borderBottom: '1px solid #eee',
                  paddingBottom: 16
                }}>
                  <div style={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                    <PrinterOutlined style={{ fontSize: 56, opacity: 0.9 }} />
                  </div>
                  <div style={{ lineHeight: 1.6, flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>
                      <IntlMessages id="closeShift.printer.deviceName" /> : {p?.name}
                      {isDefault && (
                        <span style={{
                          marginLeft: 8, fontSize: 12, fontWeight: 600,
                          background: '#e6f4ff', color: '#0958d9', padding: '2px 8px', borderRadius: 12
                        }}><IntlMessages id="closeShift.printer.default" /></span>
                      )}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>
                      <IntlMessages id="closeShift.printer.status" /> : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 12, height: 12, borderRadius: '50%', background: meta.color, display: 'inline-block' }} />
                        {meta.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      <IntlMessages id="closeShift.printer.driver" />: {p?.driverName} • <IntlMessages id="closeShift.printer.port" />: {p?.portName} • <IntlMessages id="closeShift.printer.queue" />: {p?.queueCount}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                    <Button
                      type={isReady ? 'primary' : 'default'}
                      disabled={!isReady}
                      onClick={() => handleChoosePrinter(p?.name)}
                      style={{ minWidth: 96 }}
                    >
                      <IntlMessages id="app.global.label.select" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  )
}

export default CloseShiftModal
